import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { logoUrlFromBranding, resolveClinicLogoUrl } from '@/lib/clinicBranding';
import type { Json } from '@/types/database.generated';

export interface PortalClinicItem {
  patient_id: string;
  clinic_id: string;
  clinic_name: string;
  clinic_logo: string | null;
  clinic_slug: string | null;
  clinic_address: unknown;
  online_booking_enabled: boolean;
}

export const portalClinicsQueryKey = (userId: string | null) =>
  ['portal', 'clinics', userId] as const;

/**
 * Lists all clinics where the authenticated portal user has access.
 * Fetches patients first, then clinic names in a separate query so a blocked
 * clinics embed cannot zero out the whole portal context.
 */
export function usePortalClinics() {
  const { user } = useAuth();

  return useQuery({
    queryKey: portalClinicsQueryKey(user?.id ?? null),
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<PortalClinicItem[]> => {
      if (!user?.id) return [];

      const { data: patients, error: patientsError } = await supabase
        .from('patients')
        .select('id, clinic_id')
        .eq('portal_user_id', user.id)
        .eq('status', 'active')
        .order('clinic_id');

      if (patientsError) throw patientsError;
      if (!patients?.length) return [];

      const clinicIds = [...new Set(patients.map((p) => p.clinic_id))];
      const clinicById = new Map<
        string,
        {
          name: string;
          logo: string | null;
          slug: string | null;
          address: unknown;
          online_booking_enabled: boolean;
        }
      >();

      if (clinicIds.length > 0) {
        const { data: clinicRows, error: clinicsError } = await supabase
          .from('clinics')
          .select('id, name, branding, slug, address, online_booking_enabled')
          .in('id', clinicIds);

        if (clinicsError) throw clinicsError;

        for (const row of clinicRows ?? []) {
          clinicById.set(row.id, {
            name: row.name,
            logo: resolveClinicLogoUrl(logoUrlFromBranding(row.branding as Json)),
            slug: row.slug ?? null,
            address: row.address,
            online_booking_enabled: Boolean(row.online_booking_enabled),
          });
        }
      }

      return patients.map((row) => {
        const clinic = clinicById.get(row.clinic_id);
        return {
          patient_id: row.id,
          clinic_id: row.clinic_id,
          clinic_name: clinic?.name ?? '—',
          clinic_logo: clinic?.logo ?? null,
          clinic_slug: clinic?.slug ?? null,
          clinic_address: clinic?.address ?? null,
          online_booking_enabled: clinic?.online_booking_enabled ?? false,
        };
      });
    },
    staleTime: 60_000,
  });
}
